import {useEffect, useRef} from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {openFileInViewer} from '@/app/modules/widgets/components/external-viewer/open-in-viewer'
import {useLayoutPanelsOptional} from '@/shared/context/ChatContext'

/**
 * Deep-link handler for the Results page.
 *
 * The real {@link ResultsPage} (the viewer host + its message-driven viewer manager) is
 * always mounted in {@code MasterLayout}; this element only owns the {@code /results}
 * route so it can honour a {@code ?file=<cloudPath>} query param — opening that file in a
 * viewer through the exact same pipeline as a file-manager double-click
 * ({@link openFileInViewer}), so the OBserver resolves the right viewer (works for
 * {@code .box} objects and raw files alike).
 *
 * This enables external links — e.g. from the OmicsBox MCP / chat assistant — of the form
 *   https://dev.omicsbox.biobam.com/results?file=<path-in-your-cloud-storage>
 *   (optional &name=<tab title>)
 * The path is relative to the signed-in user's own cloud root; files are resolved against
 * the caller's {@code custom:identityId}, so a user can only open their own files.
 *
 * Note: this fires only for an already-authenticated session. A logged-out visitor is
 * redirected to /signin by AppRoutes and the param is currently not preserved across login
 * (see the invitation-return pattern for how that could be added).
 */
const ResultsDeepLink = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const layout = useLayoutPanelsOptional()
  // Tracks the last file we opened, so each NEW `?file=` opens exactly once. Guards React 18
  // StrictMode's double-invoke and the strip re-render, while still re-opening when the user
  // clicks a different result link WITHOUT leaving /results. (A permanent "opened once" boolean
  // broke same-tab re-navigation: once on /results it blocked every subsequent link.)
  const lastOpenedRef = useRef<string | null>(null)

  useEffect(() => {
    const file = searchParams.get('file')
    if (!file) {
      // Param cleared (our own strip, or a clean /results) — allow re-opening later.
      lastOpenedRef.current = null
      return
    }
    if (file === lastOpenedRef.current) {
      return
    }
    lastOpenedRef.current = file

    const displayName = searchParams.get('name') ?? undefined
    void openFileInViewer({
      filename: file,
      displayName,
      navigate,
      expandCenter: layout?.expandCenter,
    })

    // Strip the deep-link params so a refresh / back-navigation doesn't re-open the file
    // and the URL settles to a clean /results. openFileInViewer navigates to /results itself.
    const next = new URLSearchParams(searchParams)
    next.delete('file')
    next.delete('name')
    setSearchParams(next, {replace: true})
  }, [searchParams, navigate, setSearchParams, layout?.expandCenter])

  return null
}

export default ResultsDeepLink
